/**
 * Patient Package Redemptions — read queries (PRD.262).
 *
 * Read-side QuerySpecs for the redemption ledger. The redemption transaction
 * (create/reverse) is performed imperatively by the web-admin redemption
 * service so PowerSync records CRUD entries per row.
 */

import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Types ───

/** A raw `patient_package_redemptions` row (SQLite representation). */
export interface PatientPackageRedemptionRow {
  id: string;
  org_id: string | null;
  patient_id: string | null;
  clinic_id: string | null;
  patient_package_id: string | null;
  patient_package_item_id: string | null;
  child_inventory_item_id: string | null;
  source_invoice_id: string | null;
  source_invoice_item_id: string | null;
  visit_id: string | null;
  redeemed_quantity: number | null;
  item_name_snapshot: string | null;
  item_code_snapshot: string | null;
  item_type_snapshot: string | null;
  unit_price_snapshot: number | null;
  covered_amount: number | null;
  redeemed_at: string | null;
  redeemed_by: string | null;
  notes: string | null;
  status: string | null;
  reversed_at: string | null;
  reversed_by: string | null;
  reverse_reason: string | null;
  reversal_of_redemption_id: string | null;
  client_reference: string | null;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
}

// ─── Queries ───

/** A patient's redemption history (newest first), excluding soft-deleted rows. */
export function qPatientPackageRedemptions(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient_package_redemptions', 'by-patient', patientId] as const,
    sql: `
      SELECT *
      FROM patient_package_redemptions
      WHERE patient_id = ?
        AND deleted_at IS NULL
      ORDER BY redeemed_at DESC
    `,
    params: [patientId],
    watch: ['patient_package_redemptions'],
  };
}

/** Redemptions for a single patient package (newest first). */
export function qRedemptionsByPackage(patientPackageId: string): QuerySpec<[string]> {
  return {
    key: ['patient_package_redemptions', 'by-package', patientPackageId] as const,
    sql: `
      SELECT *
      FROM patient_package_redemptions
      WHERE patient_package_id = ?
        AND deleted_at IS NULL
      ORDER BY redeemed_at DESC
    `,
    params: [patientPackageId],
    watch: ['patient_package_redemptions'],
  };
}
